import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { enqueueSnackbar } from '~/utils/notifications';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import NumberInput from '~/components/Inputs/NumberInput';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    keepEveryNthSliceValueState,
    minSliceInterval,
} from '~/state/atoms/toolsAtoms';

type ClearScope = 'current' | 'all' | 'nth';

const toggleGroupSx = {
    '& .MuiToggleButton-root': {
        py: 0.5,
        px: 0.75,
        flex: 1,
        fontSize: 11,
        lineHeight: 1,
        textTransform: 'none',
        whiteSpace: 'nowrap',
    },
};

export default function ClearContoursInline() {
    const mainView = useRecoilValue(mainViewState);
    const environment = useRecoilValue(environmentState);
    const selectedROI = useRecoilValue(selectedROIState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const [nthValue, setNthValue] = useRecoilState(keepEveryNthSliceValueState);

    const intl = useIntl();
    const [scope, setScope] = useState<ClearScope>('current');

    const selectedRoi = structureSet?.roiArray?.find(
        (roi) => roi.roiNumber === selectedROI
    );

    const validateCanModify = (): boolean => {
        if (!selectedRoi) {
            return false;
        }
        if (selectedRoi.approvalStatus === DICOMApprovalStatus.APPROVED) {
            enqueueSnackbar(
                <FormattedMessage id="structureCannotBeModified" />,
                { variant: 'error' }
            );
            return false;
        }
        return true;
    };

    const handleScopeChange = (
        _: React.MouseEvent<HTMLElement>,
        value: ClearScope | null
    ) => {
        if (value !== null) {
            setScope(value);
        }
    };

    const handleNthValueChange = (value: number | null) => {
        if (value !== null) {
            setNthValue(value);
        }
    };

    const handleApply = () => {
        if (!validateCanModify()) {
            return;
        }

        if (scope === 'current') {
            if (environment && mainView && selectedROI !== -1) {
                const planeCoordinates = mainView.position[2];
                environment.clearContourOnSlice(selectedROI, planeCoordinates);
            }
        } else if (scope === 'all') {
            if (environment && selectedROI !== -1) {
                environment.clearAllContours(selectedROI);
            }
        } else if (scope === 'nth') {
            if (environment && selectedROI !== -1 && nthValue > 1) {
                environment.keepEveryNthSlice(selectedROI, nthValue);
            }
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {/* Scope selector */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="body2" fontSize={12}>
                    {intl.formatMessage({ id: 'clearTool' })}
                </Typography>
                <ToggleButtonGroup
                    value={scope}
                    exclusive
                    onChange={handleScopeChange}
                    size="small"
                    sx={toggleGroupSx}
                >
                    <ToggleButton value="current">
                        {intl.formatMessage({ id: 'current' })}
                    </ToggleButton>
                    <ToggleButton value="all">
                        {intl.formatMessage({ id: 'all' })}
                    </ToggleButton>
                    <ToggleButton value="nth">
                        {intl.formatMessage({ id: 'nth' })}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Action row: optional n-th input + apply button */}
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                {scope === 'nth' && (
                    <Box sx={{ width: 64, flexShrink: 0 }}>
                        <NumberInput
                            value={nthValue}
                            onChange={handleNthValueChange}
                            min={minSliceInterval}
                            step={1}
                            precision={0}
                            fullWidth
                            inputProps={{
                                style: {
                                    fontSize: 12,
                                    padding: '4px 24px 4px 8px',
                                },
                            }}
                        />
                    </Box>
                )}
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    onClick={handleApply}
                    sx={{ fontSize: 11, textTransform: 'none' }}
                >
                    <FormattedMessage id="clearContours" />
                </Button>
            </Box>
        </Box>
    );
}
